import React, { useState } from 'react';
import { IconButton, Popover, Stack, Tooltip, Typography, styled, Box } from '@mui/material';
import { SketchPicker } from 'react-color';
import IconPickerPopover from '../../common/ui/IconPickerPopover';
import UploadIcon from "@mui/icons-material/Upload";
import { theme } from '../../theme';
import { useAppDispatch } from '../../hooks/StoreHooks';
import { uploadActionModuleIconThunk, uploadCalendarEventIconThunk, uploadServiceModuleIconThunk } from '../../store/thunk/uploaderThunk';
import { DeleteIcon } from '../../assets/icons/Icons';
import { useTranslation } from 'react-i18next';
import DefaultIcon from '../../components/common/DefaultIcon';

type IconColorPickerProps = {
    parentId: number;
    parentType: "serviceModule" | "actionModule" | "calendarEvent";
    eventId?: number;
    iconData: {
        iconName: string;
        colorHex: string;
        iconUrl: string | null;
    };
    uploadDisabled: boolean;
    onChange: (updates: { iconName: string; colorHex: string; iconUrl: string | null }) => void;
};

const IconAndColorPicker: React.FC<IconColorPickerProps> = ({
    parentId,
    parentType,
    eventId,
    iconData,
    onChange,
    uploadDisabled,
}) => {
    const dispatch = useAppDispatch();
    const { t } = useTranslation();
    const { iconName, colorHex, iconUrl } = iconData;
    const [iconPickerAnchor, setIconPickerAnchor] = useState<null | HTMLElement>(null);
    const [colorPickerAnchor, setColorPickerAnchor] = useState<null | HTMLElement>(null)
    const fileInputRef = React.useRef<HTMLInputElement>(null);
    const hasIconUrl = Boolean(iconUrl);

    const handleIconChange = (newIconName: string) => {
        setIconPickerAnchor(null);
        onChange({ ...iconData, iconName: newIconName });
    };

    const handleColorChange = (color: any) => {
        const newColor = color.hex;
        onChange({ ...iconData, colorHex: newColor });
    };

    const uploadCustomIcon = async (
        file: File,
        type: "serviceModule" | "actionModule" | "calendarEvent",
    ) => {
        const reader = new FileReader();

        reader.onloadend = async (e) => {
            const binaryString = e.target?.result as string;
            const base64Data = binaryString.split(',')[1];

            try {
                let resultAction;
                switch (type) {
                    case "serviceModule":
                        resultAction = await dispatch(uploadServiceModuleIconThunk({ serviceModuleId: parentId, base64Data }));
                        break;
                    case "actionModule":
                        resultAction = await dispatch(uploadActionModuleIconThunk({ actionModuleId: parentId, base64Data }));
                        break;
                    case "calendarEvent":
                        resultAction = await dispatch(uploadCalendarEventIconThunk({ serviceModuleId: parentId, eventId: eventId!, base64Data }));
                        break;
                    default:
                        return;
                }
                const newUrl = resultAction.payload as string | null;
                if (newUrl) {
                    onChange({ ...iconData, iconUrl: newUrl });
                }
            } catch (err) {
                console.error(t("iconPicker.uploadErrorTitle"), err);
            }
        };
        reader.readAsDataURL(file);
    };

    const deleteCustomIcon = async () => {
        try {
            switch (parentType) {
                case "serviceModule":
                    await dispatch(
                        uploadServiceModuleIconThunk({ serviceModuleId: parentId, base64Data: null })
                    );
                    break;
                case "actionModule":
                    await dispatch(
                        uploadActionModuleIconThunk({ actionModuleId: parentId, base64Data: null })
                    );
                    break;
                case "calendarEvent":
                    if (eventId) {
                        await dispatch(
                            uploadCalendarEventIconThunk({ serviceModuleId: parentId, eventId, base64Data: null })
                        );
                    }
                    break;
            }
            onChange({ ...iconData, iconUrl: null });
        } catch (err) {
            console.error(t("iconPicker.deleteErrorTitle"), err);
        }
    };

    const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
        const file = e.target.files?.[0];
        if (!file) return;
        if (!file.type.startsWith("image/")) {
            alert(t("iconPicker.fileTypeError"));
            return;
        }
        if (file.size > 1024 * 1024) {
            alert(t("iconPicker.fileSizeError"));
            return;
        }
        uploadCustomIcon(file, parentType);
    };

    return (
        <Stack direction="row" alignItems="center" gap={2}>
            <Typography variant="body1" color={'text.primary'}>
                {t("iconPicker.iconLabel")}
            </Typography>
            <Tooltip title={t("iconPicker.iconLabel")}>
                <IconButton
                    onClick={(e) => setIconPickerAnchor(e.currentTarget)}
                >
                    <DefaultIcon
                        colorHex={colorHex}
                        iconName={iconName}
                        iconUrl={iconUrl}
                    />
                </IconButton>
            </Tooltip>
            {
                !hasIconUrl && <>
                    <Typography variant="body1">{t("iconPicker.colorLabel")}</Typography>
                    <StyledColorPreview
                        color={colorHex ?? '#fff'}
                        onClick={(e) => {
                            setColorPickerAnchor(e.currentTarget);
                        }}
                        sx={{
                            cursor: hasIconUrl ? 'not-allowed' : 'pointer',
                            opacity: hasIconUrl ? 0.5 : 1,
                        }} />
                </>
            }
            {
                hasIconUrl
                    ? <Tooltip title={t("iconPicker.tooltipDeleteIcon")}>
                        <IconButton
                            onClick={deleteCustomIcon}>
                            <DeleteIcon />
                        </IconButton>
                    </Tooltip>
                    : <Tooltip
                        title={
                            uploadDisabled
                                ? t("iconPicker.tooltipUploadDisabled")
                                : t("iconPicker.tooltipUploadIcon")
                        }
                    >
                        <span>
                            <StyledUploadButton
                                disabled={uploadDisabled}
                                onClick={() => !uploadDisabled && fileInputRef.current?.click()}
                            >
                                <UploadIcon sx={{ color: theme.palette.companyMain.main }} />
                            </StyledUploadButton>
                        </span>
                    </Tooltip>
            }
            <input
                type="file"
                ref={fileInputRef}
                hidden
                accept="image/*"
                onChange={handleFileSelect}
            />

            <IconPickerPopover
                anchorEl={iconPickerAnchor}
                colorHex={colorHex}
                selectedIconName={iconName}
                onSelectIcon={handleIconChange}
                onClose={() => setIconPickerAnchor(null)}
            />

            <Popover
                open={Boolean(colorPickerAnchor)}
                anchorEl={colorPickerAnchor}
                onClose={() => setColorPickerAnchor(null)}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
            >
                <SketchPicker
                    color={colorHex}
                    onChange={handleColorChange}
                />
            </Popover>
        </Stack>
    );
};

const StyledColorPreview = styled(Box)(({ color }: { color: string }) => ({
    width: "24px",
    height: "24px",
    borderRadius: '50%',
    backgroundColor: color,
    border: `2px solid ${theme.palette.divider}`,
    cursor: "pointer",
    '&:hover': {
        boxShadow: "0 0 8px rgba(0, 0, 0, 0.15)",
        border: `2px solid ${theme.palette.primary.main}`,
        transform: 'scale(1.1)',
        transition: 'transform 0.15s ease',
    }
}));

const StyledUploadButton = styled(IconButton)(({ theme, disabled }) => ({
    transition: "opacity 0.2s ease, transform 0.2s ease",
    opacity: disabled ? 0.4 : 1,
    pointerEvents: disabled ? "auto" : "auto",
    cursor: disabled ? "not-allowed" : "pointer",
    filter: disabled ? "grayscale(100%)" : "none",
    "&:hover": {
        transform: disabled ? "none" : "scale(1.05)",
        backgroundColor: disabled ? "transparent" : theme.palette.action.hover,
    }
}));

export default IconAndColorPicker;
